import React, { useRef, useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'

const LOAD_TIMEOUT = 8000;
const INITIAL_LOAD_TIMEOUT = 10000;
const MAX_RETRIES = 3;
const PLAYBACK_STATE = {
    ENDED: 0,
    PLAYING: 1,
    PAUSED: 2,
};

const buildPlayerSrc = (videoId, { showTikTokDescription, autoplay, muted }) => {
    const params = new URLSearchParams({
        description: showTikTokDescription ? '1' : '0',
        loop: '0',
    });

    if (autoplay) params.set('autoplay', '1');
    params.set('muted', muted ? '1' : '0');

    return `https://www.tiktok.com/player/v1/${videoId}?${params.toString()}`;
};

const TikTokPlayer = ({ id, playerReady, videoEnded, showTikTokDescription, muted = false, autoplay, videoStatus, fullscreen }) => {
    const [playerReadyState, setPlayerReadyState] = useState(true);
    const [retryLimitReached, setRetryLimitReached] = useState(false);
    const iframeRef = useRef(null);
    const loadTimerRef = useRef(null);
    const initialLoadTimerRef = useRef(null);
    const retryCount = useRef(0);
    const isPlayerReadyRef = useRef(false);
    const autoplayRef = useRef(autoplay);
    const mutedRef = useRef(muted);
    const videoEndedRef = useRef(videoEnded);
    const videoStatusRef = useRef(videoStatus);
    const playRetryTimerRef = useRef(null);
    const hasReportedEndRef = useRef(false);

    autoplayRef.current = autoplay;
    mutedRef.current = muted;
    videoEndedRef.current = videoEnded;
    videoStatusRef.current = videoStatus;

    const sendPlayerMessage = useCallback((type, value) => {
        if (!iframeRef.current?.contentWindow) return;

        const message = { type, 'x-tiktok-player': true };
        if (value !== undefined) message.value = value;
        iframeRef.current.contentWindow.postMessage(message, '*');
    }, []);

    const ensureUnmuted = useCallback(() => {
        if (mutedRef.current) return;

        sendPlayerMessage('unMute');
    }, [sendPlayerMessage]);

    const scheduleUnmute = useCallback(() => {
        if (mutedRef.current) return;

        ensureUnmuted();
        setTimeout(ensureUnmuted, 100);
        setTimeout(ensureUnmuted, 500);
    }, [ensureUnmuted]);

    const notifyVideoEnded = useCallback(() => {
        if (hasReportedEndRef.current) return;

        hasReportedEndRef.current = true;
        videoEndedRef.current?.();
    }, []);

    const requestPlayback = useCallback(() => {
        if (hasReportedEndRef.current) return;

        clearTimeout(playRetryTimerRef.current);

        if (videoStatusRef.current === 'pause') {
            sendPlayerMessage('pause');
            return;
        }

        sendPlayerMessage('play');

        // TikTok's SDK can miss the first play command while initializing.
        playRetryTimerRef.current = setTimeout(() => {
            sendPlayerMessage('play');
        }, 250);
    }, [sendPlayerMessage]);

    const handler = useCallback((event) => {
        if (event.source !== iframeRef.current?.contentWindow) return;
        if (!event.data?.['x-tiktok-player']) return;

        switch (event.data.type) {
            case 'onPlayerReady':
                isPlayerReadyRef.current = true;
                if (mutedRef.current) {
                    sendPlayerMessage('mute');
                }
                setPlayerReadyState(true);
                clearTimeout(loadTimerRef.current);
                clearTimeout(initialLoadTimerRef.current);
                if (autoplayRef.current || videoStatusRef.current === 'play') requestPlayback();
                scheduleUnmute();
                break;
            case 'onStateChange':
                if (event.data.value === PLAYBACK_STATE.PLAYING) {
                    scheduleUnmute();
                } else if (event.data.value === PLAYBACK_STATE.ENDED) {
                    notifyVideoEnded();
                }
                break;
            case 'onMute':
                if (!mutedRef.current && event.data.value) {
                    ensureUnmuted();
                }
                break;
            case 'onCurrentTime':
                if (
                    event.data.value.duration > 0 &&
                    event.data.value.currentTime >= event.data.value.duration
                ) {
                    notifyVideoEnded();
                }
                break;
            default:
                break;
        }
    }, [ensureUnmuted, notifyVideoEnded, requestPlayback, scheduleUnmute, sendPlayerMessage]);

    const triggerRetry = () => {
        if (retryCount.current >= MAX_RETRIES) {
            setRetryLimitReached(true);
            return;
        }

        isPlayerReadyRef.current = false;
        retryCount.current++;
        setPlayerReadyState(false);
        setTimeout(() => setPlayerReadyState(true), 0);
    }

    const startLoadTimer = () => {
        // onLoad fired, so clear the initial timeout (request didn't hang)
        clearTimeout(initialLoadTimerRef.current);
        loadTimerRef.current = setTimeout(triggerRetry, LOAD_TIMEOUT);
    }

    useEffect(() => {
        window.addEventListener('message', handler)
        return () => {
            window.removeEventListener('message', handler)
        }
    }, [handler]);

    useEffect(() => {
        isPlayerReadyRef.current = false;
        hasReportedEndRef.current = false;
        retryCount.current = 0;
        setRetryLimitReached(false);
        clearTimeout(playRetryTimerRef.current);
    }, [id]);

    useEffect(() => {
        if (!isPlayerReadyRef.current) return;

        if (videoStatus === 'pause') {
            sendPlayerMessage('pause');
        } else if (videoStatus === 'play') {
            requestPlayback();
        }
    }, [videoStatus, requestPlayback, sendPlayerMessage]);

    useEffect(() => {
        return () => {
            clearTimeout(playRetryTimerRef.current);
        };
    }, []);

    useEffect(() => {
        playerReady && playerReady();
    }, [playerReadyState, playerReady]);

    // Initial timeout - catches cases where onLoad never fires (e.g., 504 gateway timeout hanging)
    useEffect(() => {
        if (retryLimitReached) return;

        initialLoadTimerRef.current = setTimeout(() => {
            triggerRetry();
        }, INITIAL_LOAD_TIMEOUT);

        return () => {
            clearTimeout(initialLoadTimerRef.current);
        };
    }, [id, playerReadyState]);


    return (
        <div className={`flex flex-col items-center justify-center ${fullscreen ? 'h-screen' : ''} ${playerReadyState ? '' : 'hidden'}`}>
            <div className={`${fullscreen ? 'aspect-[9/16] h-full' : 'w-full'}`}>
                {playerReadyState && !retryLimitReached &&
                    <iframe
                        id="tiktok-player"
                        className="w-full h-full"
                        ref={iframeRef}
                        onLoad={startLoadTimer}
                        src={buildPlayerSrc(id, { showTikTokDescription, autoplay, muted })}
                        allow="autoplay; encrypted-media; fullscreen; gyroscope; accelerometer; cross-origin-isolated; microphone; camera"
                        allowFullScreen={true}
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation allow-presentation"
                        title="TikTok video preview"
                    ></iframe>
                }
                {retryLimitReached && (
                    <div className="text-center text-gray-400">
                        Failed to load TikTok player.
                    </div>
                )}
            </div>
        </div>
    )
}

TikTokPlayer.propTypes = {
    id: PropTypes.string,
    playerReady: PropTypes.func.isRequired,
    videoEnded: PropTypes.func.isRequired,
    showTikTokDescription: PropTypes.bool.isRequired,
    fullscreen: PropTypes.bool,
}

export default TikTokPlayer